/* globals globalNavigate */
import React, { useState, useEffect, useContext } from "react";
import Modal from "common/Modal";
import Request from "common/Request";
import Form from "common/Form";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import NotForOperators from "common/NotForOperators";
import {Header} from "common/Header";
import MarkdownHelpModal from "common/MarkdownHelpModal";

export class InvalidFieldValue extends Error {
  constructor(field, reason) {
    super(`The "${field}" field ${reason}`);
  }
}

export class InvalidFieldValues extends Error {
  constructor(reason) {
    super(`${reason}`);
  }
}

export const DefaultContextMenu = ({ doLoad = doesNothing, Help = null }) => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      {Help === null ? null :
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
      </li>
      }
      <li>
        <a
          onClick={doLoad}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const WarnPendingChanges = () => (
  <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 align-left">
    <div id="profThrouPendingChangesShow">
      <input
        type="radio"
        className="radio-col-red"
        checked
        onChange={() => {}}
      />
      <label id="profThrouPendingChanges" htmlFor="profThrouPendingChanges">
        <b>Pending Changes</b>
      </label>
    </div>
  </div>
);

const titleForError = (error) => {
  if (error instanceof InvalidFieldValue){
    return "INVALID FIELD VALUE";
  }
  if (error instanceof InvalidFieldValues){
    return "INVALID FIELD VALUES";
  }
  return "APPLY FAILED";
}

const ReportResultModal = ({ result = null, onClose = doesNothing }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(result !== null && (result.ok || result.error));
  }, [result]);
  return visible && result !== null ? (
    <Modal
      title={result.error ? titleForError(result.error) : "COMMAND SUCESSFUL"}
      superIcon={result.error ? "error_outline" : undefined}
      onClose={onClose}
      content={() => (
        <>
          {result.ok ? <div className="row">{result.ok.message}</div> : null}
          {result.error ? (
            <div className="error align-left">
              {result.error.message === undefined
                ? result.error
                : result.error.message}
            </div>
          ) : null}
        </>
      )}
      closeLabel={"CLOSE"}
    />
  ) : null;
};

export const applyParams = (child, params = {}) =>
  React.isValidElement(child) ? React.cloneElement(child, params) : child;

const MayBeAForm = ({ wrap = true, onSubmit = null, children }) =>
  wrap === true ? <Form onSubmit={onSubmit}>{children}</Form> : children;

const CloseOrReturn = ({returnView, close}) =>
  returnView != null ? (
    <button
      type="button"
      className="btn btn-link waves-effect"
      onClick={() => globalNavigate(returnView)}
    >
      Return
    </button>
  ) : close !== undefined ? (
    <button
      type="button"
      className="btn btn-link waves-effect"
      onClick={close}
    >
      Close
    </button>
  ) : null

export const SettingsPanel = ({
  reload,
  children,
  apply = () => Promise.resolve(null),
  defaults = {},
  wrapIntoForm = true,
  actionsClassName = "",
  applyActionClassName = "",
  applyLabel = "Apply",
  noDiscard = false,
  noPristine = false,
  local = {}, setLocal = doesNothing,
  PendingChangesIndicator = WarnPendingChanges,
  ...settings
}) => {
  const [state, setState] = useState({pristine: true});
  const [internalDefaults, setInternalDefaults] = useState(defaults);
  const actions = useContext(ActionsContext);
  const [result, setResult] = useState(defaults);

  useEffect(() => {
    setInternalDefaults(defaults);
    setState({
      pristine: noPristine ? false: true,
      ...defaults,
    });
  }, [JSON.stringify(defaults)]);

  const onChange = (event, setting = {}) => {
    if (event) {
      const { name, type } = event.target;
      let { value } = event.target;
      value = type === "checkbox" ? event.target.checked === true : value;
      setState((previous) => ({ ...previous, [name]: value, pristine: false }));
    } else {
      const { name, value } = setting;
      setState((previous) => ({ ...previous, [name]: value, pristine: false }));
    }
  };
  const onStateUpdate = (update) =>
    setState((previous) => ({ ...previous, ...update, pristine: false }));

  const notifyError = (error) => {
    setResult({ error });
    throw error;
  };

  useEffect(() => {
    actions.send("state-changed", state);
  }, [state]);
  const clearResult = () => setResult(null);

  const doApply = (formSettings = {}) => {
    const newSettings = { ...state, ...formSettings };
    return apply(newSettings, internalDefaults, state)
      .then((result) => {
        if(result?.error){
          return;
        }

        const { pristine, ...appliedSettings } = newSettings;
        setInternalDefaults(appliedSettings);
        setState((previous) => ({ ...previous, pristine: true }));
      })
      .catch(notifyError);
  };

  return (
    <MayBeAForm wrap={wrapIntoForm} onSubmit={doApply}>
      <div className="body table-responsive">
        {React.Children.map(children, (child) =>
          applyParams(child, { onChange, state, defaults, onStateUpdate, local, setLocal })
        )}
      </div>
      <div className="control modal-footer">
        {noPristine === true || state.pristine !== true ? (
          <React.Fragment>
            {PendingChangesIndicator === null ? null : (
              <PendingChangesIndicator />
            )}
            <NotForOperators>
              <button
                type="submit"
                className={`btn btn-link waves-effect ${applyActionClassName} ${actionsClassName}`}
                onClick={wrapIntoForm ? null : () => doApply()}
              >
                {applyLabel}
              </button>
            </NotForOperators>
            {noDiscard === true ? null : (
              <button
                type="button"
                className={`btn btn-link waves-effect ${actionsClassName}`}
                onClick={() => reload()}
              >
                Discard
              </button>
            )}
          </React.Fragment>
        ) : <CloseOrReturn {...settings}/>}
      </div>
      <ReportResultModal result={result} onClose={clearResult} />
    </MayBeAForm>
  );
};

export const NoContextMenu = () => null;

const doesNothing = () => {};

export const SettingsViewLayout = styled.div`
`
export const SettingsView = ({
  title = "title not set",
  titleClassName = "",
  className = "container-lg",
  subtitle = null,
  load = doesNothing,
  outcome,
  apply = () => Promise.resolve(null),
  clear = doesNothing,
  ContextMenu = DefaultContextMenu,
  noHeader,
  children,
  id,
  returnView,
  bqnSelector=true,
  noMargin = false,
  mdHelp = null,
  ...settings
}) => {
  const [request, setRequest] = useState(null);
  const [local, setLocal] = useState({})
  const [generation, setGeneration] = useState(0);
  const actions = useContext(ActionsContext);
  // When outcome is given, data is owned by the caller (e.g. withReloading)
  // and reloads are requested through the "do-load" signal; bumping the
  // generation remounts the panel so the defaults are restored right away.
  const doLoad = (initial) => {
    if (outcome !== undefined) {
      setGeneration((previous) => previous + 1);
      actions.send("do-load");
    } else {
      setRequest(load(initial));
    }
  };

  useEffect(() => {
    if (outcome !== undefined) return undefined;
    doLoad(true);

    return actions.recv("do-load", () =>
      doLoad()
    );
  }, []);

  const showSettings = (result) => (
    <SettingsPanel
      {...result}
      reload={doLoad}
      apply={apply}
      clear={clear}
      defaults={result}
      returnView={returnView}
      {...settings}
      local={local}
      setLocal={setLocal}
    >
      {React.Children.map(children, (child) =>
        applyParams(child, { actions })
      )}
    </SettingsPanel>
  );

  return outcome === undefined && request === null ? null : (
    <SettingsViewLayout className={className} id={id}>
      <div className="row clearfix">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className={`card ${noMargin ? "no-margin" : ""}`}>
            {!noHeader && (
              <Header
                title={title}
                titleClassName={titleClassName}
                subtitle={subtitle}
                backButton={!!returnView}
                contextMenu={<ContextMenu doLoad={() => doLoad()} Help={mdHelp || null} />}
                bqnSelector={bqnSelector}
                returnUrl={returnView}
              />
            )}
            {outcome !== undefined
              ? <React.Fragment key={generation}>
                  {showSettings(outcome)}
                </React.Fragment>
              : <Request during={request}>{showSettings}</Request>}
          </div>
        </div>
      </div>
      {mdHelp === null ? null : <MarkdownHelpModal {...{title, ...settings}} source={mdHelp} />}
    </SettingsViewLayout>
  );
};

export default SettingsView;
